import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, dashboardPath } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Welcome back!");
      const pending = localStorage.getItem("sj_pending_plan");
      if (user.role === "client" && pending) { navigate("/app/client/plans?plan=" + pending); return; }
      navigate(dashboardPath(user.role));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-slate-950">
        <img src="https://images.pexels.com/photos/9170385/pexels-photo-9170385.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940" alt="Properties" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 to-slate-950/30" />
        <div className="relative p-12 flex flex-col h-full justify-between">
          <Link to="/" className="flex items-center gap-2 text-white">
            <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
            <span className="font-head font-bold text-lg">SJ Property Care</span>
          </Link>
          <div>
            <h2 className="font-head text-3xl font-semibold text-white leading-tight">Property Maintenance.<br />One Request. Every Trade.</h2>
            <p className="mt-4 text-slate-300 max-w-sm">Manage repairs, contractors, estimates, and invoices in one centralized platform.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Building2 className="w-5 h-5 text-white" /></div>
              <span className="font-head font-bold text-lg text-slate-900">SJ Property Care</span>
            </Link>
          </div>
          <h1 className="font-head text-3xl font-semibold text-slate-900">Sign In</h1>
          <p className="mt-2 text-slate-500">Access your dashboard.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" data-testid="login-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-rose-600">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 h-11">
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-500 space-y-1">
            <p>New property manager? <Link to="/register/client" className="text-blue-600 font-medium">Register here</Link></p>
            <p>Contractor? <Link to="/register/contractor" className="text-blue-600 font-medium">Apply to join</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}
